import React, { useState, useEffect, useRef } from 'react';
import { streakService } from '../services/streakService';

export default function DailyGoalBar({ currentUser, authUsers = [] }) {
  const userId = currentUser?.id || null;

  const [data, setData] = useState(() => {
    const result = streakService.refresh(userId, authUsers);
    const goals = streakService.getGoals(userId);
    return { ...result, ...goals };
  });

  const [editingGoal, setEditingGoal] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [celebrate, setCelebrate] = useState(false);
  const editRef = useRef(null);
  const prevHitRef = useRef(false);

  useEffect(() => {
    if (editingGoal && editRef.current) editRef.current.focus();
  }, [editingGoal]);

  useEffect(() => {
    function refresh() {
      const result = streakService.refresh(userId, authUsers);
      const goals = streakService.getGoals(userId);
      const next = { ...result, ...goals };
      if (next.todayHit && !prevHitRef.current) {
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 2800);
      }
      prevHitRef.current = next.todayHit;
      setData(next);
    }
    refresh();
    window.addEventListener('crm-activities-changed', refresh);
    return () => window.removeEventListener('crm-activities-changed', refresh);
  }, [userId, authUsers]);

  function startEdit(field, currentVal) {
    setEditingGoal(field);
    setEditVal(String(currentVal));
  }

  function confirmEdit(field) {
    const num = parseInt(editVal, 10);
    if (!isNaN(num) && num > 0) {
      const patch = field === 'calls' ? { goalCallsDay: num } : { goalApptsDay: num };
      streakService.setGoals(userId, patch);
      const result = streakService.refresh(userId, authUsers);
      const goals = streakService.getGoals(userId);
      setData({ ...result, ...goals });
    }
    setEditingGoal(null);
    setEditVal('');
  }

  const {
    callsToday = 0,
    apptsToday = 0,
    streak = 0,
    goalCallsDay = 10,
    goalApptsDay = 2,
    todayHit = false,
    nextMilestone = null,
    currentMilestone = null,
  } = data;

  const callsPct = Math.min(100, Math.round((callsToday / Math.max(1, goalCallsDay)) * 100));
  const apptsPct = Math.min(100, Math.round((apptsToday / Math.max(1, goalApptsDay)) * 100));
  const callsDone = callsToday >= goalCallsDay;
  const apptsDone = apptsToday >= goalApptsDay;

  return (
    <div className="gl-card" style={{
      borderRadius: 14,
      padding: '11px 18px',
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      flexWrap: 'wrap',
      transition: 'box-shadow .4s',
      ...(celebrate ? { boxShadow: '0 0 32px rgba(22,163,74,0.28), 0 4px 20px rgba(22,163,74,0.12), inset 0 1px 0 rgba(255,255,255,0.95)' } : {}),
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
        Tagesziele
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: callsDone ? '#16a34a' : 'var(--text)', fontSize: 13 }}>{callsToday}</span>
          {' / '}
          {editingGoal === 'calls' ? (
            <input
              ref={editRef}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onBlur={() => confirmEdit('calls')}
              onKeyDown={e => { if (e.key === 'Enter') confirmEdit('calls'); if (e.key === 'Escape') setEditingGoal(null); }}
              style={{ width: 36, height: 18, borderRadius: 4, border: '1.5px solid var(--accent)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 11, textAlign: 'center', padding: '0 2px', outline: 'none', fontFamily: 'inherit' }}
            />
          ) : (
            <span
              title="Tagesziel Anrufe bearbeiten"
              onClick={() => startEdit('calls', goalCallsDay)}
              style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: 'var(--text3)' }}
            >{goalCallsDay}</span>
          )}
          {' '}Anrufe
        </div>
        <div style={{ flex: 1, height: 7, position: 'relative', minWidth: 60 }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: 4, background: 'var(--bg4)' }} />
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${callsPct}%`, borderRadius: 4, transition: 'width .5s var(--motion-ease)', background: callsDone ? 'linear-gradient(90deg, #16a34a, #22c55e)' : 'linear-gradient(90deg, #0f8a53, #2fce87)', boxShadow: callsPct > 5 ? (callsDone ? '0 0 8px rgba(34,197,94,0.55)' : '0 0 8px rgba(47,206,135,0.55)') : 'none' }}>
            {callsPct > 5 && <div style={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: callsDone ? '#22c55e' : '#2fce87', boxShadow: `0 0 10px ${callsDone ? '#22c55e' : '#2fce87'}`, border: '2px solid rgba(255,255,255,0.9)', zIndex: 1 }} />}
          </div>
        </div>
        {callsDone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>}
      </div>

      <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: apptsDone ? '#16a34a' : 'var(--text)', fontSize: 13 }}>{apptsToday}</span>
          {' / '}
          {editingGoal === 'appts' ? (
            <input
              ref={editRef}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onBlur={() => confirmEdit('appts')}
              onKeyDown={e => { if (e.key === 'Enter') confirmEdit('appts'); if (e.key === 'Escape') setEditingGoal(null); }}
              style={{ width: 36, height: 18, borderRadius: 4, border: '1.5px solid var(--accent)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 11, textAlign: 'center', padding: '0 2px', outline: 'none', fontFamily: 'inherit' }}
            />
          ) : (
            <span
              title="Tagesziel Termine bearbeiten"
              onClick={() => startEdit('appts', goalApptsDay)}
              style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: 'var(--text3)' }}
            >{goalApptsDay}</span>
          )}
          {' '}Termine
        </div>
        <div style={{ flex: 1, height: 7, position: 'relative', minWidth: 60 }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: 4, background: 'var(--bg4)' }} />
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${apptsPct}%`, borderRadius: 4, transition: 'width .5s var(--motion-ease)', background: apptsDone ? 'linear-gradient(90deg, #16a34a, #22c55e)' : 'linear-gradient(90deg, #7a5fb8, #ab9bd6)', boxShadow: apptsPct > 5 ? (apptsDone ? '0 0 8px rgba(34,197,94,0.55)' : '0 0 8px rgba(171,155,214,0.55)') : 'none' }}>
            {apptsPct > 5 && <div style={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: apptsDone ? '#22c55e' : '#ab9bd6', boxShadow: `0 0 10px ${apptsDone ? '#22c55e' : '#ab9bd6'}`, border: '2px solid rgba(255,255,255,0.9)', zIndex: 1 }} />}
          </div>
        </div>
        {apptsDone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>}
      </div>

      <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, minWidth: 120 }}>
        <span style={{ fontSize: 18 }}>{currentMilestone?.emoji || '🔥'}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: streak > 0 ? '#f97316' : 'var(--text3)', lineHeight: 1 }}>
            {streak} {streak === 1 ? 'Tag' : 'Tage'}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>
            {todayHit ? 'Streak heute gesichert' : 'Streak · Ziel heute erreichen'}
          </div>
        </div>
      </div>

      {nextMilestone && (
        <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>
          Noch <strong style={{ color: 'var(--accent)' }}>{nextMilestone.daysLeft}</strong> Tag{nextMilestone.daysLeft === 1 ? '' : 'e'} bis {nextMilestone.emoji} {nextMilestone.label}
        </div>
      )}

      {celebrate && (
        <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', flexShrink: 0, animation: 'pulse 1s ease infinite' }}>
          🎉 Tagesziel geschafft!
        </div>
      )}
    </div>
  );
}
